// src/pages/StaffManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Tooltip,
  Popover,
  Badge,
  ConfigProvider,
  Tag as AntTag,
} from "antd";
import {
  listStaffUsers,
  createStaffUser,
  updateStaffUser,
  deleteStaffUser,
} from "../../api/userApi";
import "../style/theme-overrides.css";

const BRAND = {
  primary: "#FFA64D",
  primaryHover: "#FF9226",
  primaryActive: "#F5821F",
};

const t = (s) => (window.translate ? window.translate(s) : s);

const toText = (v) =>
  typeof v === "object" && v ? v.en || v.ar || "" : v || "";

const normalizeLoc = (v, fallback = "") => ({
  en: (v ?? fallback) || "",
  ar: (v ?? fallback) || "",
});

const PERMISSION_MODULES = {
  dashboard: {
    en: "Dashboard",
    ar: "لوحة التحكم",
    backend: "dashboard",
    actions: ["read", "write"],
  },
  products: {
    en: "Products",
    ar: "المنتجات",
    backend: "products",
    actions: ["read", "write"],
  },
  expiredProducts: {
    en: "Expired Products",
    ar: "Expired Products",
    backend: "expire",
    actions: ["read", "write"],
  },
  barcode: {
    en: "Print Barcode",
    ar: "Print Barcode",
    backend: "barcode",
    actions: ["read", "write"],
  },
  variantAttributes: {
    en: "Variant Attributes",
    ar: "Variant Attributes",
    backend: "variantAttributes",
    actions: ["read", "write"],
  },
  catalogUploads: {
    en: "Catalog Uploads",
    ar: "Catalog Uploads",
    backend: "catalogUploads",
    actions: ["read", "write"],
  },
  brands: {
    en: "Brands",
    ar: "العلامات التجارية",
    backend: "brands",
    actions: ["read", "write"],
  },
  categories: {
    en: "Categories",
    ar: "الفئات",
    backend: "categories",
    actions: ["read", "write"],
  },
  subCategories: {
    en: "Sub Categories",
    ar: "Sub Categories",
    backend: "subCategory",
    actions: ["read", "write"],
  },
  units: {
    en: "Units",
    ar: "الوحدات",
    backend: "units",
    actions: ["read", "write"],
  },
  taxes: {
    en: "Tax",
    ar: "Tax",
    backend: "taxes",
    actions: ["read", "write"],
  },
  warehouses: {
    en: "Warehouses",
    ar: "المستودعات",
    backend: "warehouses",
    actions: ["read", "write"],
  },
  stock: {
    en: "Manage Stock",
    ar: "المخزون",
    backend: "stock",
    actions: ["read", "write"],
  },
  stockAdjustments: {
    en: "Stock Adjustments",
    ar: "Stock Adjustments",
    backend: "stockAdjustment",
    actions: ["read", "write"],
  },
  stockTransfers: {
    en: "Stock Transfers",
    ar: "Stock Transfers",
    backend: "stockTransfer",
    actions: ["read", "write"],
  },
  lowStock: {
    en: "Low Stock",
    ar: "Low Stock",
    backend: "lowStock",
    actions: ["read", "write"],
  },
  outOfStock: {
    en: "Out Of Stock",
    ar: "Out Of Stock",
    backend: "outOfStock",
    actions: ["read", "write"],
  },
  inventoryLogs: {
    en: "Inventory Logs",
    ar: "Inventory Logs",
    backend: "inventoryLogs",
    actions: ["read", "write"],
  },
  stockHistory: {
    en: "Stock History",
    ar: "Stock History",
    backend: "stockHistory",
    actions: ["read", "write"],
  },
  suppliers: {
    en: "Suppliers",
    ar: "الموردون",
    backend: "suppliers",
    actions: ["read", "write"],
  },
  purchases: {
    en: "Purchases",
    ar: "Purchases",
    backend: "purchases",
    actions: ["read", "write"],
  },
  purchaseOrderReport: {
    en: "Purchase Order Report",
    ar: "Purchase Order Report",
    backend: "purchaseOrderReport",
    actions: ["read", "write"],
  },
  customers: {
    en: "Customers",
    ar: "العملاء",
    backend: "customers",
    actions: ["read", "write"],
  },
  billers: {
    en: "Billers",
    ar: "المفوترون",
    backend: "billers",
    actions: ["read", "write"],
  },
  orders: {
    en: "Orders",
    ar: "الطلبات",
    backend: "orders",
    actions: ["read", "write"],
  },
  sales: {
    en: "Sales",
    ar: "المبيعات",
    backend: "sales",
    actions: ["read", "write"],
  },
  posSales: {
    en: "POS Sales",
    ar: "مبيعات نقاط البيع",
    backend: "pos-orders",
    actions: ["read", "write"],
  },
  onlineOrders: {
    en: "Online Orders",
    ar: "Online Orders",
    backend: "onlineOrders",
    actions: ["read", "write"],
  },
  salesReport: {
    en: "Sales Report",
    ar: "تقرير المبيعات",
    backend: "salesReport",
    actions: ["read", "write"],
  },
  purchaseReport: {
    en: "Purchase Report",
    ar: "Purchase Report",
    backend: "purchaseReport",
    actions: ["read", "write"],
  },
  inventoryReport: {
    en: "Inventory Report",
    ar: "Inventory Report",
    backend: "inventoryReport",
    actions: ["read", "write"],
  },
  soldStockReport: {
    en: "Sold Stock Report",
    ar: "Sold Stock Report",
    backend: "soldStockReport",
    actions: ["read", "write"],
  },
  invoiceReport: {
    en: "Invoice Report",
    ar: "Invoice Report",
    backend: "invoiceReport",
    actions: ["read", "write"],
  },
  supplierReport: {
    en: "Supplier Report",
    ar: "Supplier Report",
    backend: "supplierReport",
    actions: ["read", "write"],
  },
  supplierDueReport: {
    en: "Supplier Due Report",
    ar: "Supplier Due Report",
    backend: "supplierDueReport",
    actions: ["read", "write"],
  },
  customerReport: {
    en: "Customer Report",
    ar: "Customer Report",
    backend: "customerReport",
    actions: ["read", "write"],
  },
  customerDueReport: {
    en: "Customer Due Report",
    ar: "Customer Due Report",
    backend: "customerDueReport",
    actions: ["read", "write"],
  },
  productReport: {
    en: "Product Report",
    ar: "Product Report",
    backend: "productReport",
    actions: ["read", "write"],
  },
  productExpiryReport: {
    en: "Product Expiry Report",
    ar: "Product Expiry Report",
    backend: "productExpiryReport",
    actions: ["read", "write"],
  },
  productQuantityAlertReport: {
    en: "Product Quantity Alert Report",
    ar: "Product Quantity Alert Report",
    backend: "productQuantityAlertReport",
    actions: ["read", "write"],
  },
  expenseReport: {
    en: "Expense Report",
    ar: "Expense Report",
    backend: "expenseReport",
    actions: ["read", "write"],
  },
  incomeReport: {
    en: "Income Report",
    ar: "Income Report",
    backend: "incomeReport",
    actions: ["read", "write"],
  },
  purchaseTaxReport: {
    en: "Purchase Tax Report",
    ar: "Purchase Tax Report",
    backend: "purchaseTaxReport",
    actions: ["read", "write"],
  },
  profitLossReport: {
    en: "Profit & Loss Report",
    ar: "Profit & Loss Report",
    backend: "profitLossReport",
    actions: ["read", "write"],
  },
  annualReport: {
    en: "Annual Report",
    ar: "Annual Report",
    backend: "annualReport",
    actions: ["read", "write"],
  },
  saleTaxReport: {
    en: "Sales Tax Report",
    ar: "Sales Tax Report",
    backend: "saleTaxReport",
    actions: ["read", "write"],
  },
  invoices: {
    en: "Invoices",
    ar: "الفواتير",
    backend: "invoices",
    actions: ["read", "write"],
  },
  invoiceDetail: {
    en: "Invoice Detail",
    ar: "تفاصيل الفاتورة",
    backend: "invoiceDetail",
    actions: ["read", "write"],
  },
  transactions: {
    en: "Transactions",
    ar: "المعاملات",
    backend: "transactions",
    actions: ["read", "write"],
  },
  transactionReports: {
    en: "Transaction Reports",
    ar: "Transaction Reports",
    backend: "transactionReports",
    actions: ["read", "write"],
  },
  transactionHistory: {
    en: "Transaction History",
    ar: "Transaction History",
    backend: "transactionHistory",
    actions: ["read", "write"],
  },
  users: {
    en: "Users",
    ar: "المستخدمون",
    backend: "users",
    actions: ["read", "write", "manage"],
  },
  profile: {
    en: "Profile",
    ar: "Profile",
    backend: "profile",
    actions: ["read", "write"],
  },
  vendorLink: {
    en: "Vendor Link",
    ar: "Vendor Link",
    backend: "vendorLink",
    actions: ["read", "write"],
  },
  stores: {
    en: "Stores",
    ar: "المتاجر",
    backend: "stores",
    actions: ["read", "write"],
  },
  warranties: {
    en: "Warranties",
    ar: "الضمانات",
    backend: "warranties",
    actions: ["read", "write"],
  },
  saas: {
    en: "Subscription",
    ar: "الاشتراك",
    backend: "saas",
    actions: ["write"],
  },
};

const ROLE_OPTIONS = [
  { label: "staff", value: "staff" },
  { label: "demo", value: "demo" },
];

const GROUPS = [
  {
    titleKey: "Inventory",
    modules: [
      "products",
      "expiredProducts",
      "barcode",
      "variantAttributes",
      "catalogUploads",
      "brands",
      "categories",
      "subCategories",
      "units",
      "taxes",
      "warehouses",
      "stock",
      "stockAdjustments",
      "stockTransfers",
      "lowStock",
      "outOfStock",
      "inventoryLogs",
      "stockHistory",
    ],
  },
  {
    titleKey: "Parties",
    modules: ["suppliers", "customers", "billers"],
  },
  {
    titleKey: "Sales & Finance",
    modules: [
      "purchases",
      "purchaseOrderReport",
      "sales",
      "posSales",
      "orders",
      "onlineOrders",
      "invoices",
      "invoiceDetail",
      "transactions",
      "transactionReports",
      "transactionHistory",
    ],
  },
  {
    titleKey: "Reports",
    modules: [
      "salesReport",
      "purchaseReport",
      "inventoryReport",
      "stockHistory",
      "soldStockReport",
      "invoiceReport",
      "supplierReport",
      "supplierDueReport",
      "customerReport",
      "customerDueReport",
      "productReport",
      "productExpiryReport",
      "productQuantityAlertReport",
      "expenseReport",
      "incomeReport",
      "purchaseTaxReport",
      "profitLossReport",
      "annualReport",
      "saleTaxReport",
    ],
  },
  {
    titleKey: "Organization",
    modules: [
      "users",
      "profile",
      "vendorLink",
      "stores",
      "warranties",
      "dashboard",
      "saas",
    ],
  },
];

const BACKEND_TO_UI_MODULE = Object.fromEntries(
  Object.entries(PERMISSION_MODULES).map(([uiKey, cfg]) => [
    cfg.backend,
    uiKey,
  ]),
);

const PERM_ALIAS = {
  dashboard: "dashboard",
  products: "products",
  product: "products",
  expire: "expire",
  expired: "expire",
  expiredProducts: "expire",
  barcode: "barcode",
  printBarcode: "barcode",
  variant: "variantAttributes",
  variantAttributes: "variantAttributes",
  "variant-attributes": "variantAttributes",
  uploads: "catalogUploads",
  upload: "catalogUploads",
  catalogUploads: "catalogUploads",
  "catalog-image": "catalogUploads",
  fileupload: "catalogUploads",
  brands: "brands",
  brand: "brands",
  categories: "categories",
  category: "categories",
  subCategory: "subCategory",
  subcategories: "subCategory",
  subCategories: "subCategory",
  "sub-categories": "subCategory",
  units: "units",
  unit: "units",
  warehouses: "warehouses",
  warehouse: "warehouses",
  suppliers: "suppliers",
  supplier: "suppliers",
  purchases: "purchases",
  purchase: "purchases",
  purchaseList: "purchases",
  purchaselist: "purchases",
  purchaseOrder: "purchases",
  purchaseOrders: "purchases",
  purchaseorder: "purchases",
  purchaseReturn: "purchases",
  purchaseReturns: "purchases",
  purchasereturn: "purchases",
  "purchase-list": "purchases",
  "purchase-order": "purchases",
  "purchase-return": "purchases",
  "purchase-returns": "purchases",
  purchaseorderreport: "purchaseOrderReport",
  purchaseOrderReport: "purchaseOrderReport",
  purchaseOrderReports: "purchaseOrderReport",
  purchaseReport: "purchaseReport",
  purchasereport: "purchaseReport",
  "purchase-order-report": "purchaseOrderReport",
  "purchase-report": "purchaseReport",
  customers: "customers",
  customer: "customers",
  billers: "billers",
  biller: "billers",
  stock: "stock",
  stockAdjustment: "stockAdjustment",
  stockAdjustments: "stockAdjustment",
  "stock-adjustments": "stockAdjustment",
  stockTransfer: "stockTransfer",
  stockTransfers: "stockTransfer",
  "stock-transfer": "stockTransfer",
  inventoryLogs: "inventoryLogs",
  inventoryLog: "inventoryLogs",
  log: "inventoryLogs",
  stockHistory: "stockHistory",
  stockhistory: "stockHistory",
  "stock-history": "stockHistory",
  stockHistoryReport: "stockHistory",
  "stock-history-report": "stockHistory",
  lowStock: "lowStock",
  "low-stock": "lowStock",
  outOfStock: "outOfStock",
  "out-of-stock": "outOfStock",
  stockManagement: "stock",
  manageStock: "stock",
  orders: "orders",
  salesOrder: "orders",
  salesOrders: "orders",
  onlineOrder: "onlineOrders",
  onlineOrders: "onlineOrders",
  "sales-orders": "orders",
  sales: "sales",
  sale: "sales",
  posSales: "pos-orders",
  posSale: "pos-orders",
  posOrders: "pos-orders",
  "pos-orders": "pos-orders",
  posOrder: "pos-orders",
  "pos-order": "pos-orders",
  salesReport: "salesReport",
  "sales-report": "salesReport",
  salesreport: "salesReport",
  inventoryReport: "inventoryReport",
  "inventory-report": "inventoryReport",
  inventoryreport: "inventoryReport",
  soldStockReport: "soldStockReport",
  "sold-stock-report": "soldStockReport",
  soldstockreport: "soldStockReport",
  soldstock: "soldStockReport",
  "sold-stock": "soldStockReport",
  invoiceReport: "invoiceReport",
  "invoice-report": "invoiceReport",
  invoicereport: "invoiceReport",
  invoicereportnew: "invoiceReport",
  supplierReport: "supplierReport",
  "supplier-report": "supplierReport",
  supplierreport: "supplierReport",
  supplierDueReport: "supplierDueReport",
  "supplier-due-report": "supplierDueReport",
  supplierduereport: "supplierDueReport",
  customerReport: "customerReport",
  "customer-report": "customerReport",
  customerreport: "customerReport",
  customerDueReport: "customerDueReport",
  "customer-due-report": "customerDueReport",
  customerduereport: "customerDueReport",
  productReport: "productReport",
  "product-report": "productReport",
  productreport: "productReport",
  productExpiryReport: "productExpiryReport",
  "product-expiry-report": "productExpiryReport",
  productexpiredreport: "productExpiryReport",
  productQuantityAlertReport: "productQuantityAlertReport",
  "product-quantity-report": "productQuantityAlertReport",
  productquantityreport: "productQuantityAlertReport",
  expenseReport: "expenseReport",
  "expense-report": "expenseReport",
  expensereport: "expenseReport",
  incomeReport: "incomeReport",
  "income-report": "incomeReport",
  incomereport: "incomeReport",
  tax: "taxes",
  taxes: "taxes",
  purchaseTaxReport: "purchaseTaxReport",
  taxReport: "purchaseTaxReport",
  "purchase-tax-report": "purchaseTaxReport",
  purchasetaxreport: "purchaseTaxReport",
  "purchase-tax": "purchaseTaxReport",
  purchasetax: "purchaseTaxReport",
  "tax-report": "purchaseTaxReport",
  taxreport: "purchaseTaxReport",
  taxReports: "purchaseTaxReport",
  "tax-reports": "purchaseTaxReport",
  profitLossReport: "profitLossReport",
  "profit-loss-report": "profitLossReport",
  profitloss: "profitLossReport",
  annualReport: "annualReport",
  "annual-report": "annualReport",
  annualreport: "annualReport",
  saleTaxReport: "saleTaxReport",
  salesTaxReport: "saleTaxReport",
  "sales-tax": "saleTaxReport",
  "sales-tax-report": "saleTaxReport",
  "sale-tax-report": "saleTaxReport",
  salereport: "saleTaxReport",
  invoiceDetail: "invoiceDetail",
  "invoice-detail": "invoiceDetail",
  invoiceDetails: "invoiceDetail",
  invoicedetails: "invoiceDetail",
  invoices: "invoices",
  invoice: "invoices",
  salesInvoices: "invoices",
  transactions: "transactions",
  transactionHistory: "transactionHistory",
  transactionReports: "transactionReports",
  transactionReport: "transactionReports",
  "transaction-reports": "transactionReports",
  userSales: "transactionHistory",
  users: "users",
  user: "users",
  profile: "profile",
  profiles: "profile",
  vendor: "vendorLink",
  vendorLink: "vendorLink",
  "vendor-link": "vendorLink",
  "users-role": "users",
  staffManagement: "users",
  "staff-management": "users",
  stores: "stores",
  store: "stores",
  warranties: "warranties",
  warranty: "warranties",
  saas: "saas",
};

const mapModuleToPerm = (module) => PERM_ALIAS[module] || module;

const VALID_PERMISSION_CODES = new Set(
  Object.values(PERMISSION_MODULES).flatMap((cfg) =>
    cfg.actions.map((action) => `${cfg.backend}:${action}`),
  ),
);

const normalizePermCodes = (arr = []) =>
  Array.from(
    new Set(
      (Array.isArray(arr) ? arr : [])
        .map((code) => {
          const [rawModule, rawAction = "read"] = String(code).split(":");
          const moduleKey = mapModuleToPerm(rawModule);
          const action = rawAction || "read";
          return `${moduleKey}:${action}`;
        })
        .filter((code) => VALID_PERMISSION_CODES.has(code)),
    ),
  );

const ACTION_LABELS = {
  read: { en: "Read", ar: "قراءة" },
  write: { en: "Write", ar: "كتابة" },
  manage: { en: "Manage", ar: "إدارة" },
};

const getActionLabel = (action) => t(ACTION_LABELS[action]?.en || action);

const actionSortValue = (action) => {
  if (action === "read") return 1;
  if (action === "write") return 2;
  if (action === "manage") return 3;
  return 99;
};

const buildPermissionSummary = (permissions = []) => {
  const normalized = normalizePermCodes(permissions);

  const grouped = new Map();
  const counts = { read: 0, write: 0, manage: 0, other: 0 };

  normalized.forEach((code) => {
    const [backendModule, action = "read"] = String(code).split(":");

    if (!grouped.has(backendModule)) grouped.set(backendModule, new Set());
    grouped.get(backendModule).add(action);

    if (action === "read") counts.read += 1;
    else if (action === "write") counts.write += 1;
    else if (action === "manage") counts.manage += 1;
    else counts.other += 1;
  });

  const modules = Array.from(grouped.entries())
    .map(([backendModule, actionSet]) => {
      const uiKey = BACKEND_TO_UI_MODULE[backendModule];
      return {
        moduleKey: backendModule,
        moduleName: t(PERMISSION_MODULES[uiKey]?.en || backendModule),
        actions: Array.from(actionSet).sort(
          (a, b) => actionSortValue(a) - actionSortValue(b),
        ),
      };
    })
    .sort((a, b) => a.moduleName.localeCompare(b.moduleName));

  return {
    total: normalized.length,
    counts,
    modules,
  };
};

function PermissionSummaryCell({ permissions }) {
  const summary = buildPermissionSummary(permissions);

  if (!summary.total) return <span className="text-muted">—</span>;

  const previewModules = summary.modules.slice(0, 3);
  const hiddenModulesCount = Math.max(0, summary.modules.length - 3);

  const popoverContent = (
    <div className="permission-popover-content">
      <div className="permission-popover-title">
        {t("All Permissions")} ({summary.total})
      </div>
      <div className="permission-popover-list">
        {summary.modules.map((m) => (
          <div key={m.moduleKey} className="permission-popover-item">
            <span className="permission-popover-module">{m.moduleName}</span>
            <span className="permission-popover-actions">
              {m.actions.map((action) => (
                <Tag
                  key={`${m.moduleKey}:${action}`}
                  className={`permission-action-tag permission-action-${action}`}
                >
                  {getActionLabel(action)}
                </Tag>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="permission-summary-cell">
      <div className="permission-summary-metrics">
        <span className="permission-metric permission-metric-read">
          {t("Read")} {summary.counts.read}
        </span>
        <span className="permission-metric permission-metric-write">
          {t("Write")} {summary.counts.write}
        </span>
        {summary.counts.manage > 0 && (
          <span className="permission-metric permission-metric-manage">
            {t("Manage")} {summary.counts.manage}
          </span>
        )}
      </div>

      <div className="permission-summary-tags">
        {previewModules.map((m) => (
          <Tag key={m.moduleKey} className="permission-module-tag">
            <span className="permission-module-name">{m.moduleName}</span>
            <span className="permission-module-actions">
              {m.actions
                .map((a) => getActionLabel(a).charAt(0).toUpperCase())
                .join("/")}
            </span>
          </Tag>
        ))}

        {hiddenModulesCount > 0 && (
          <Popover
            content={popoverContent}
            trigger="click"
            placement="bottomLeft"
            overlayClassName="permission-popover-overlay"
          >
            <Button type="link" size="small" className="permission-more-btn">
              {t("View All")} +{hiddenModulesCount}
            </Button>
          </Popover>
        )}
      </div>
    </div>
  );
}

PermissionSummaryCell.propTypes = {
  permissions: PropTypes.arrayOf(PropTypes.string),
};

PermissionSummaryCell.defaultProps = {
  permissions: [],
};

function Pill({ active, children, onClick, type = "read" }) {
  return (
    <AntTag.CheckableTag
      checked={active}
      onChange={onClick}
      className="permission-pill"
    >
      <Badge
        status={
          active
            ? type === "write"
              ? "processing"
              : type === "manage"
                ? "warning"
                : "success"
            : "default"
        }
      />
      <span
        className={`ms-1 ${
          active
            ? type === "write"
              ? "text-primary"
              : type === "manage"
                ? "text-warning"
                : "text-success"
            : ""
        }`}
      >
        {children}
      </span>
    </AntTag.CheckableTag>
  );
}

Pill.propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["read", "write", "manage"]),
};

Pill.defaultProps = {
  type: "read",
};

function PermissionGrid({ form }) {
  const [q, setQ] = useState("");

  const getPerms = () =>
    normalizePermCodes(form.getFieldValue("permissions") || []);

  const setPerms = (arr) =>
    form.setFieldsValue({ permissions: normalizePermCodes(arr) });

  const toggle = (uiModuleKey, action) => {
    const cfg = PERMISSION_MODULES[uiModuleKey];
    if (!cfg) return;

    const backendModule = cfg.backend;
    const perms = new Set(getPerms());

    const code = `${backendModule}:${action}`;
    const readCode = `${backendModule}:read`;
    const writeCode = `${backendModule}:write`;

    if (perms.has(code)) {
      perms.delete(code);

      if (action === "read" && perms.has(writeCode)) {
        perms.delete(writeCode);
      }
    } else {
      perms.add(code);

      if (action === "write" && cfg.actions.includes("read")) {
        perms.add(readCode);
      }
    }

    setPerms([...perms]);
  };

  const removeAll = () => setPerms([]);

  const selectAll = () => {
    const all = Object.values(PERMISSION_MODULES).flatMap((cfg) => {
      const codes = [];

      if (cfg.actions.includes("read")) codes.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) {
        if (cfg.actions.includes("read")) codes.push(`${cfg.backend}:read`);
        codes.push(`${cfg.backend}:write`);
      }
      if (cfg.actions.includes("manage")) codes.push(`${cfg.backend}:manage`);

      return codes;
    });

    setPerms(all);
  };

  const presetViewOnly = () => {
    const view = Object.values(PERMISSION_MODULES)
      .filter((cfg) => cfg.actions.includes("read"))
      .map((cfg) => `${cfg.backend}:read`);

    setPerms(view);
  };

  const presetInventoryAdmin = () => {
    const invModules = [
      "products",
      "expiredProducts",
      "barcode",
      "variantAttributes",
      "catalogUploads",
      "brands",
      "categories",
      "subCategories",
      "units",
      "taxes",
      "warehouses",
      "stock",
      "stockAdjustments",
      "stockTransfers",
      "lowStock",
      "outOfStock",
      "inventoryLogs",
      "stockHistory",
      "suppliers",
      "purchases",
    ];

    const codes = invModules.flatMap((m) => {
      const cfg = PERMISSION_MODULES[m];
      if (!cfg) return [];
      const out = [];
      if (cfg.actions.includes("read")) out.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) out.push(`${cfg.backend}:write`);
      return out;
    });

    setPerms(codes);
  };

  const presetSalesClerk = () => {
    const salesModules = [
      "sales",
      "posSales",
      "orders",
      "onlineOrders",
      "invoices",
      "invoiceDetail",
      "salesReport",
      "customers",
      "billers",
      "transactions",
      "transactionReports",
      "transactionHistory",
    ];

    const codes = salesModules.flatMap((m) => {
      const cfg = PERMISSION_MODULES[m];
      if (!cfg) return [];
      const out = [];
      if (cfg.actions.includes("read")) out.push(`${cfg.backend}:read`);
      if (cfg.actions.includes("write")) out.push(`${cfg.backend}:write`);
      return out;
    });

    setPerms(codes);
  };

  const perms = getPerms();

  const match = (uiModuleKey) => {
    if (!q.trim()) return true;
    const cfg = PERMISSION_MODULES[uiModuleKey];
    const hay =
      `${t(cfg?.en || uiModuleKey)} ${t(cfg?.hint || "")}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: BRAND.primary,
          colorPrimaryHover: BRAND.primaryHover,
          colorPrimaryActive: BRAND.primaryActive,
        },
      }}
    >
      <div className="permission-grid-shell">
        <div className="permission-toolbar">
          <div className="permission-toolbar-left">
            <Input
              placeholder={t("Search modules...")}
              allowClear
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="permission-search-input"
            />

            <Tooltip title={t("View-only")}>
              <Button onClick={presetViewOnly}>{t("View-only")}</Button>
            </Tooltip>

            <Tooltip title={t("Inventory Admin")}>
              <Button onClick={presetInventoryAdmin}>
                {t("Inventory Admin")}
              </Button>
            </Tooltip>

            <Tooltip title={t("Sales Clerk")}>
              <Button onClick={presetSalesClerk}>{t("Sales Clerk")}</Button>
            </Tooltip>
          </div>

          <div className="permission-toolbar-right">
            <div className="selected-permission-badge">
              <Badge
                count={perms.length}
                style={{ backgroundColor: "#fe9f43" , marginRight: "10px" }}
              >
                <span className="selected-permission-label">
                  {t("Selected")}
                </span>
              </Badge>
            </div>

            <Button onClick={removeAll}>{t("Clear")}</Button>

            <Button type="primary" ghost onClick={selectAll}>
              {t("Select All")}
            </Button>
          </div>
        </div>

        <Row gutter={[16, 16]}>
          {GROUPS.map((g) => {
            const visibleModules = g.modules.filter(match);
            if (!visibleModules.length) return null;

            return (
              <Col xs={24} lg={12} key={g.titleKey}>
                <Card
                  size="small"
                  className="permission-section-card"
                  title={<span className="fw-semibold">{t(g.titleKey)}</span>}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[12, 12]}>
                    {visibleModules.map((uiModuleKey) => {
                      const cfg = PERMISSION_MODULES[uiModuleKey];
                      const name = t(cfg.en);
                      const backend = cfg.backend;

                      const hasRead = perms.includes(`${backend}:read`);
                      const hasWrite = perms.includes(`${backend}:write`);
                      const hasManage = perms.includes(`${backend}:manage`);

                      return (
                        <Col xs={24} key={uiModuleKey}>
                          <div className="permission-module-row">
                            <div className="permission-module-info">
                              <div className="permission-module-name-text">
                                {name}
                              </div>
                              {cfg.hint ? (
                                <div className="permission-module-hint">
                                  {t(cfg.hint)}
                                </div>
                              ) : null}
                            </div>

                            <div className="permission-module-actions-wrap">
                              {cfg.actions.includes("read") && (
                                <Pill
                                  active={hasRead}
                                  onClick={() => toggle(uiModuleKey, "read")}
                                  type="read"
                                >
                                  {t("Read")}
                                </Pill>
                              )}

                              {cfg.actions.includes("write") && (
                                <Pill
                                  active={hasWrite}
                                  onClick={() => toggle(uiModuleKey, "write")}
                                  type="write"
                                >
                                  {t("Write")}
                                </Pill>
                              )}

                              {cfg.actions.includes("manage") && (
                                <Pill
                                  active={hasManage}
                                  onClick={() => toggle(uiModuleKey, "manage")}
                                  type="manage"
                                >
                                  {t("Manage")}
                                </Pill>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    </ConfigProvider>
  );
}

PermissionGrid.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldsValue: PropTypes.func.isRequired,
  }).isRequired,
};

export default function StaffManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  const isRtlUi = useMemo(() => {
    if (typeof document === "undefined") return false;

    return (
      document.body?.classList?.contains("layout-mode-rtl") ||
      document.documentElement?.getAttribute("dir") === "rtl" ||
      (typeof localStorage !== "undefined" &&
        (localStorage.getItem("lang") || "").toLowerCase().startsWith("ar"))
    );
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    const res = await listStaffUsers();

    if (!res.success) {
      message.error(res.error || t("Failed to load users"));
      setRows([]);
    } else {
      setRows(Array.isArray(res.data) ? res.data : []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      username: "",
      phone: "",
      role: "staff",
      readOnly: false,
      active: true,
      permissions: [],
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      email: row.email || "",
      password: "",
      firstName: toText(row.firstName),
      lastName: toText(row.lastName),
      username: toText(row.username),
      phone: row.phone || "",
      role: row.role || "staff",
      readOnly: !!row.readOnly,
      active: !!row.active,
      permissions: normalizePermCodes(row.permissions || []),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const refreshPageAfterSave = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
    window.location.reload();
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        email: values.email.trim(),
        ...(values.password?.trim()
          ? { password: values.password.trim() }
          : {}),
        firstName: normalizeLoc(values.firstName?.trim() || ""),
        lastName: normalizeLoc(values.lastName?.trim() || ""),
        username: normalizeLoc(
          (values.username || "").trim() ||
            (values.email || "").split("@")[0] ||
            "",
        ),
        phone: values.phone?.trim() || "",
        role: values.role,
        readOnly: !!values.readOnly,
        active: !!values.active,
        permissions: normalizePermCodes(values.permissions || []),
      };

      if (!editing) {
        if (!payload.password) {
          message.warning(t("Password is required for new users"));
          setSaving(false);
          return;
        }

        const res = await createStaffUser(payload);
        if (!res.success) throw new Error(res.error || t("Create failed"));
        message.success(t("User created"));
      } else {
        const res = await updateStaffUser(editing._id, payload);
        if (!res.success) throw new Error(res.error || t("Update failed"));
        message.success(t("User updated"));
      }

      refreshPageAfterSave();
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err.message || t("Save failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    const res = await deleteStaffUser(id);

    if (!res.success) {
      message.error(res.error || t("Delete failed"));
    } else {
      message.success(t("User deleted"));
      fetchRows();
    }
  };

  const columns = [
    {
      title: t("Email"),
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: t("Name"),
      dataIndex: "name",
      render: (_, r) => `${toText(r.firstName)} ${toText(r.lastName)}`.trim(),
      sorter: (a, b) =>
        `${toText(a.firstName)} ${toText(a.lastName)}`
          .trim()
          .localeCompare(`${toText(b.firstName)} ${toText(b.lastName)}`.trim()),
    },
    {
      title: t("Username"),
      dataIndex: "username",
      render: (_, r) => toText(r.username),
      sorter: (a, b) => toText(a.username).localeCompare(toText(b.username)),
    },
    {
      title: t("Role"),
      dataIndex: "role",
      render: (role) => <Tag>{role}</Tag>,
      sorter: (a, b) => (a.role || "").localeCompare(b.role || ""),
    },
    {
      title: t("Read Only"),
      dataIndex: "readOnly",
      render: (val) => (val ? t("Yes") : t("No")),
      sorter: (a, b) => Number(!!a.readOnly) - Number(!!b.readOnly),
    },
    {
      title: t("Active"),
      dataIndex: "active",
      render: (val) => (
        <Tag color={val ? "green" : "default"}>
          {val ? t("Active") : t("Inactive")}
        </Tag>
      ),
      sorter: (a, b) => Number(!!a.active) - Number(!!b.active),
    },
    {
      title: t("Permissions"),
      dataIndex: "permissions",
      width: 420,
      render: (perms) => <PermissionSummaryCell permissions={perms} />,
      sorter: (a, b) =>
        buildPermissionSummary(a.permissions).total -
        buildPermissionSummary(b.permissions).total,
    },
    {
      title: t("Action"),
      key: "action",
      width: 180,
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEdit(record)}>
            {t("Edit")}
          </Button>
          <Popconfirm
            title={t("Delete User")}
            description={t("Are you sure you want to delete this user?")}
            okText={t("Delete")}
            cancelText={t("Cancel")}
            onConfirm={() => onDelete(record._id)}
          >
            <Button size="small" danger>
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper staff-management-page">
      <style>
        {`
          /* ✅ FIX 1: Force modal mask and wrap to cover the ENTIRE viewport including sidebar */
          .staff-user-modal {
            /* nothing special needed here – fixes are on mask and wrap below */
          }

          .staff-user-modal .ant-modal-mask,
          body > .ant-modal-mask {
            position: fixed !important;
            inset: 0 !important;
            z-index: 1050 !important;
            background: rgba(0, 0, 0, 0.45) !important;
            backdrop-filter: blur(2px);
          }

          .staff-user-modal .ant-modal-wrap,
          body > .ant-modal-wrap {
            position: fixed !important;
            inset: 0 !important;
            z-index: 1051 !important;
            overflow: auto !important;
            outline: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 24px !important;
          }

          .staff-user-modal .ant-modal {
            top: 0 !important;
            margin: 0 auto !important;
            padding-bottom: 0 !important;
            max-width: min(1120px, calc(100vw - 48px)) !important;
            position: relative !important;
          }

          .staff-user-modal .ant-modal-content {
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18);
          }

          .staff-user-modal .ant-modal-header {
            padding: 20px 24px 12px;
            border-bottom: 1px solid #f0f0f0;
            background: #fff;
          }

          .staff-user-modal .ant-modal-title {
            font-size: 18px;
            font-weight: 700;
          }

          .staff-user-modal .ant-modal-body {
            padding: 20px 24px;
            max-height: calc(100vh - 170px);
            overflow-y: auto;
            background: #fff;
          }

          .staff-user-modal .ant-modal-footer {
            padding: 14px 24px 20px;
            border-top: 1px solid #f0f0f0;
            background: #fff;
          }

          .staff-user-modal .form-section-card {
            border: 1px solid #eceff3;
            border-radius: 16px;
            padding: 16px;
            margin-bottom: 16px;
            background: #fff;
          }

          .staff-user-modal .form-section-title {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 14px;
            color: #111827;
          }

          .staff-user-modal .switch-card {
            height: 100%;
            min-height: 86px;
            border: 1px solid #eceff3;
            border-radius: 14px;
            background: #fafafa;
            padding: 12px 14px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .staff-user-modal .switch-card-label {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .staff-user-modal .switch-card-label strong {
            font-size: 14px;
            color: #111827;
          }

          .staff-user-modal .switch-card-label span {
            font-size: 12px;
            color: #6b7280;
          }

          /* ── page layout ── */
          .staff-management-page .users-page-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            flex-wrap: wrap;
            margin-bottom: 20px;
          }

          .staff-management-page .users-page-title h4 {
            margin-bottom: 4px;
          }

          .staff-management-page .users-page-title h6 {
            margin-bottom: 0;
            color: #6b7280;
            font-weight: 400;
          }

          .staff-management-page .users-header-actions {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .staff-management-page .users-card {
            border: 1px solid #eef0f4;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 8px 30px rgba(16, 24, 40, 0.04);
          }

          .staff-management-page .users-card .card-header {
            background: #fff;
            border-bottom: 1px solid #f0f0f0;
            padding: 16px 20px;
          }

          .staff-management-page .users-card .card-body {
            padding: 18px;
          }

          .staff-management-page .users-seat-badge {
            display: inline-flex;
            align-items: center;
            border-radius: 999px;
            padding: 8px 12px;
            background: #fff7ed;
            color: #c56a10;
            border: 1px solid #fed7aa;
            font-size: 12px;
            font-weight: 600;
          }

          /* ── permission grid ── */
          .permission-grid-shell {
            background: #fafafa;
            border: 1px solid #eceff3;
            border-radius: 16px;
            padding: 16px;
          }

          .permission-toolbar {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 14px;
            flex-wrap: wrap;
            margin-bottom: 16px;
          }

          .permission-toolbar-left,
          .permission-toolbar-right {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
          }

          .permission-search-input {
            width: 260px;
            max-width: 100%;
          }

          .selected-permission-badge {
            min-width: fit-content;
          }

          .selected-permission-label {
            display: inline-block;
            padding: 0 10px;
            line-height: 28px;
            background: #fff;
            border: 1px solid #f0f0f0;
            border-radius: 999px;
            font-weight: 600;
          }

          .permission-section-card {
            height: 100%;
            border-radius: 16px;
            border: 1px solid #eceff3;
            box-shadow: none;
          }

          .permission-module-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 12px 14px;
            border-radius: 14px;
            border: 1px solid #eef1f4;
            background: #f8fafc;
            min-height: 70px;
          }

          .permission-module-info {
            min-width: 0;
            flex: 1;
          }

          .permission-module-name-text {
            font-weight: 600;
            color: #111827;
            line-height: 1.4;
            word-break: break-word;
          }

          .permission-module-hint {
            color: #6b7280;
            font-size: 12px;
            margin-top: 4px;
          }

          .permission-module-actions-wrap {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            flex-wrap: wrap;
            min-width: 180px;
          }

          .permission-pill {
            border-radius: 999px !important;
            padding: 4px 10px !important;
            margin-inline-start: 0 !important;
            margin-inline-end: 0 !important;
            border: 1px solid #e5e7eb !important;
            background: #fff !important;
          }

          .permission-summary-cell {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .permission-summary-metrics {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
          }

          .permission-summary-tags {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
          }

          .permission-metric {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 600;
          }

          .permission-metric-read {
            background: #ecfdf3;
            color: #027a48;
          }

          .permission-metric-write {
            background: #eff6ff;
            color: #175cd3;
          }

          .permission-metric-manage {
            background: #fff7ed;
            color: #c2410c;
          }

          .permission-module-tag {
            border-radius: 999px !important;
            padding: 4px 10px !important;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: #fff !important;
            border: 1px solid #e5e7eb !important;
          }

          .permission-module-name {
            max-width: 180px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .permission-module-actions {
            font-size: 11px;
            font-weight: 700;
            color: #667085;
          }

          .permission-more-btn {
            padding-inline: 4px !important;
          }

          .permission-popover-content {
            max-width: 380px;
          }

          .permission-popover-title {
            font-weight: 700;
            margin-bottom: 10px;
          }

          .permission-popover-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-height: 320px;
            overflow: auto;
          }

          .permission-popover-item {
            display: flex;
            justify-content: space-between;
            gap: 10px;
            align-items: flex-start;
            padding: 8px 0;
            border-bottom: 1px dashed #eceff3;
          }

          .permission-popover-item:last-child {
            border-bottom: none;
          }

          .permission-popover-module {
            font-weight: 600;
            flex: 1;
          }

          .permission-popover-actions {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
            justify-content: flex-end;
          }

          .permission-action-tag {
            border-radius: 999px;
            margin-inline-end: 0;
          }

          .permission-action-read {
            background: #ecfdf3;
            color: #027a48;
            border-color: #abefc6;
          }

          .permission-action-write {
            background: #eff6ff;
            color: #175cd3;
            border-color: #bfdcff;
          }

          .permission-action-manage {
            background: #fff7ed;
            color: #c2410c;
            border-color: #fdba74;
          }

          .staff-management-page .ant-table-wrapper .ant-table {
            border-radius: 14px;
            overflow: hidden;
          }

          .staff-management-page .ant-table-thead > tr > th {
            background: #fafafa !important;
            font-weight: 700;
          }

          .staff-management-page .ant-btn-primary {
            box-shadow: none;
          }

          .staff-management-page .ant-btn-primary:hover,
          .staff-management-page .ant-btn-primary:focus {
            box-shadow: none;
          }

          @media (max-width: 991px) {
            .permission-module-row {
              flex-direction: column;
              align-items: stretch;
            }

            .permission-module-actions-wrap {
              justify-content: flex-start;
              min-width: 0;
            }
          }

          @media (max-width: 768px) {
            .staff-management-page .users-header-actions {
              width: 100%;
            }

            .staff-management-page .users-header-actions > * {
              flex: 1 1 auto;
            }

            .permission-toolbar-left,
            .permission-toolbar-right {
              width: 100%;
            }

            .permission-search-input {
              width: 100%;
            }

            .staff-user-modal .ant-modal-wrap {
              padding: 12px !important;
            }

            .staff-user-modal .ant-modal {
              max-width: calc(100vw - 24px) !important;
            }

            .staff-user-modal .ant-modal-body {
              padding: 16px;
              max-height: calc(100vh - 140px);
            }

            .staff-user-modal .ant-modal-header,
            .staff-user-modal .ant-modal-footer {
              padding-inline: 16px;
            }
          }
        `}
      </style>

      <div className="content">
        <div className="users-page-header">
          <div className="users-page-title">
            <h4>{t("Users, Roles & Permissions")}</h4>
            <h6>{t("Invite staff, edit access, and manage seats")}</h6>
          </div>

          <div className="users-header-actions">
            <Link to="/profile" className="btn btn-light border">
              <i className="ti ti-arrow-left me-1" />
              {t("Back to Profile")}
            </Link>

            <Button
              type="primary"
              onClick={openCreate}
              style={{
                backgroundColor: "#FE9F43",
                borderColor: "#FE9F43",
                padding: "10px 20px",
              }}
            >
              <i className="ti ti-user-plus me-1" />
              {t("Add User")}
            </Button>
          </div>
        </div>

        <div className="card users-card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div />
            <div>
              <span className="users-seat-badge">
                {t("Seats are enforced by owner's limit")}
              </span>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey={(r) => r._id}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: 1100 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/*
        ✅ FIX 1: getContainer={() => document.body} forces Ant Design to mount
        the modal's mask and wrap directly on <body> instead of inside the
        page-wrapper div. This means the overlay escapes the sidebar's stacking
        context and covers the full viewport correctly.
      */}
      <Modal
        centered
        open={modalOpen}
        title={editing ? t("Edit User") : t("Add User")}
        okText={t("Save")}
        cancelText={t("Cancel")}
        confirmLoading={saving}
        onCancel={closeModal}
        onOk={onSave}
        destroyOnClose
        width={1120}
        className={`staff-user-modal ${isRtlUi ? "rtl-modal" : ""}`}
        maskClosable={false}
        getContainer={() => document.body}
        style={{ top: 0 }}
        zIndex={1050}
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            email: "",
            password: "",
            firstName: "",
            lastName: "",
            username: "",
            phone: "",
            role: "staff",
            readOnly: false,
            active: true,
            permissions: [],
          }}
        >
          <div className="form-section-card">
            <div className="form-section-title">{t("Basic Information")}</div>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("Email")}
                  name="email"
                  rules={[
                    { required: true, message: t("Email is required") },
                    { type: "email", message: t("Enter a valid email") },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={t("Password")}
                  name="password"
                  tooltip={editing ? t("Leave blank to keep current") : ""}
                  rules={
                    editing
                      ? []
                      : [{ required: true, message: t("Password is required") }]
                  }
                >
                  <Input.Password />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={t("First Name")} name="firstName">
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={t("Last Name")} name="lastName">
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={t("Username")} name="username">
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item label={t("Phone")} name="phone">
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label={t("Role")}
                  name="role"
                  rules={[{ required: true, message: t("Role is required") }]}
                >
                  <Select
                    options={ROLE_OPTIONS.map((o) => ({
                      value: o.value,
                      label: t(o.label),
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={6}>
                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <Form.Item label=" " style={{ marginBottom: 0 }}>
                      <div className="switch-card">
                        <div className="switch-card-label">
                          <strong>{t("Read Only")}</strong>
                          <span>{t("Allow read access only")}</span>
                        </div>

                        <Form.Item
                          name="readOnly"
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>

              <Col xs={24} md={6}>
                <Form.Item shouldUpdate noStyle>
                  {() => (
                    <Form.Item label=" " style={{ marginBottom: 0 }}>
                      <div className="switch-card">
                        <div className="switch-card-label">
                          <strong>{t("Active")}</strong>
                          <span>{t("Enable this account")}</span>
                        </div>

                        <Form.Item
                          name="active"
                          valuePropName="checked"
                          noStyle
                        >
                          <Switch />
                        </Form.Item>
                      </div>
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="form-section-card" style={{ marginBottom: 0 }}>
            <div className="form-section-title">{t("Permissions")}</div>

            <Form.Item name="permissions" style={{ marginBottom: 0 }}>
              <Form.Item noStyle shouldUpdate>
                {() => <PermissionGrid form={form} />}
              </Form.Item>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

